import { AgenturStore } from '../agentur.store';
import { SessionStore } from '../session.store';
import { runInAction, observable, makeObservable, action, computed } from 'mobx';
import { AgenturModel, AgtType } from 'app/models/agentur.model';
import { BaseUiStore } from './base.ui.store';
import { PersonalUiStore } from './personal.ui.store';
import { PersonalVM } from 'app/models/personal/personal.vm';
import { AgtDigitalreportUiStore, DigitalReportVM } from './agt.digitalreport.ui.store';
import { AgenturListUiStore } from './agt.list.ui.store';
import { AgtProdUiStore } from './agt.prod.ui.store';
import { AgtBestandUiStore } from './agt.bestand.ui.store';
import Config from 'Config';

/**
 * ViewModel for Nachfolgeplanung (Succession Planning)
 * Combines data from Personal, DigitalReport, and Agentur stores
 * to show agencies where the owner is approaching retirement age (55+)
 */
export class NachfolgePlanungVm {
	agt: AgenturModel;
	personal?: PersonalVM;
	digitalReport?: DigitalReportVM;
	gesamtbewertungVJ: number;
	bestandSachVJ: number;

	constructor(agt: AgenturModel, personal?: PersonalVM, digitalReport?: DigitalReportVM, gesamtbewertungVJ?: number, bestandSachVJ?: number) {
		makeObservable(this);
		this.agt = agt;
		this.personal = personal;
		this.digitalReport = digitalReport;
		this.gesamtbewertungVJ = gesamtbewertungVJ || 0;
		this.bestandSachVJ = bestandSachVJ || 0;
	}

	@computed
	get agtId() {
		return this.agt.agtId;
	}

	@computed
	get agtName() {
		return this.agt.name;
	}

	@computed
	get inhaberName() {
		return this.personal?.fullName || this.agt.name;
	}

	@computed
	get inhaberAge() {
		return this.personal?.age || 0;
	}

	@computed
	get prozDigiId18() {
		return this.digitalReport?.pos?.prozDigiId18 || 0;
	}

	@computed
	get prozWewe18() {
		return this.digitalReport?.pos?.prozWewe18 || 0;
	}

	@computed
	get prozMaz18() {
		return this.digitalReport?.pos?.prozMaz18 || 0;
	}

	@computed
	get anzKunden18() {
		return this.digitalReport?.pos?.anzKunden18 || 0;
	}

	@computed
	get focusThemen() {
		const fc = this.agt.focusThemen || [];
		return fc.filter((f) => ['Nachfolgeplanung', 'Neugründung'].includes(f.def.group));
	}

	@computed
	get hasFocusThema() {
		return this.focusThemen.length > 0;
	}
}

/**
 * UI Store for Bestandsnachfolgeplanung
 * Shows agencies where the owner is 55 years or older
 * Combines data from PersonalUiStore, AgtDigitalreportUiStore, and AgenturListUiStore
 */
export class NachfolgeplanungUiStore extends BaseUiStore<NachfolgePlanungVm> {
	name = 'NachfolgeplanungUiStore';

	session: SessionStore;
	agenturStore: AgenturStore;
	personalUiStore: PersonalUiStore;
	agtDigitalreportUiStore: AgtDigitalreportUiStore;
	agenturListUiStore: AgenturListUiStore;
	agtProdUiStore: AgtProdUiStore;
	agtBestandUiStore: AgtBestandUiStore;

	@observable
	minAge: number = 55;

	@observable
	maxAge: number = 100;

	constructor(
		session: SessionStore,
		agenturStore: AgenturStore,
		personalUiStore: PersonalUiStore,
		agtDigitalreportUiStore: AgtDigitalreportUiStore,
		agenturListUiStore: AgenturListUiStore,
		agtProdUiStore: AgtProdUiStore,
		agtBestandUiStore: AgtBestandUiStore,
	) {
		super();
		makeObservable(this);
		this.session = session;
		this.agenturStore = agenturStore;
		this.personalUiStore = personalUiStore;
		this.agtDigitalreportUiStore = agtDigitalreportUiStore;
		this.agenturListUiStore = agenturListUiStore;
		this.agtProdUiStore = agtProdUiStore;
		this.agtBestandUiStore = agtBestandUiStore;
	}

	async _baseLoad() {
		// Load personal data to get ages
		await this.personalUiStore.load();

		// Load agentur list (which includes focusThemen via docs)
		await this.agenturListUiStore.load();

		// Load digital report data for the specified BM
		const digitalReports = await this.agtDigitalreportUiStore.loadByMaxBm();

		// Load Gesamtbewertung for previous year (VJ = Vorjahr)
		const vjYear = Config.currentPlanYear - 1;
		const gesamtbewertungVJ = await this.agtProdUiStore.findByPlanKey('gesamtbewertung', vjYear);

		// Load Bestand data (includes bestandVJ)
		await this.agtBestandUiStore.load();
		const bestandItems = this.agtBestandUiStore.items;

		// Get all agencies (only real agencies, not GS/VD)
		const agts = await this.agenturStore.findAll();
		const filteredAgts = agts.filter((x) => x.agtType === AgtType.agt);

		// Get all personal data
		const personalItems = this.personalUiStore.items;

		// Create maps for O(1) lookup
		const digitalReportMap = new Map(digitalReports.map((dr) => [dr.agt.agtId, dr]));
		const gesamtbewertungMap = new Map(gesamtbewertungVJ.map((g) => [g.agtId, g.posVal?.ergebnis || 0]));
		const bestandMap = new Map(bestandItems.map((b) => [b.agtId, b.bestandVJ.val.bt || 0]));

		const res: NachfolgePlanungVm[] = [];
		filteredAgts.forEach((agt) => {
			// Find the owner (Teilhaber/GV/HV) for this agency who is 55+
			// Look for personal entries associated with this agency
			const agtPersonal = personalItems.filter((p) => p.agtId === agt.agtId);

			// Find the main owner - typically GV or HV (Generalvertreter or Hauptvertreter)
			const owner =
				agtPersonal.find((p) => {
					const bezeichnung = p.stellenBezeichnung.toUpperCase();
					return bezeichnung.indexOf('GV') >= 0 || bezeichnung.indexOf('HV') >= 0;
				}) || agtPersonal[0]; // Fallback to first person if no GV/HV found

			// Only include if owner is within valid age range (minAge to maxAge)
			if (owner && owner.age >= this.minAge && owner.age <= this.maxAge) {
				const digitalReport = digitalReportMap.get(agt.agtId);
				const gbVJ = gesamtbewertungMap.get(agt.agtId) || 0;
				const bestandSachVJ = bestandMap.get(agt.agtId) || 0;

				// Get the enriched agt from agenturListUiStore (which has focusThemen)
				const enrichedAgt = this.agenturListUiStore.items.find((a) => a.agtId === agt.agtId) || agt;

				const vm = new NachfolgePlanungVm(enrichedAgt, owner, digitalReport, gbVJ, bestandSachVJ);
				res.push(vm);
			}
		});

		runInAction(() => {
			this.items = res;
		});
	}

	@computed
	get currentBm() {
		return this.agtDigitalreportUiStore.maxBmCurrent;
	}

	@computed
	get bmOpts() {
		return this.agtDigitalreportUiStore.bmOpts;
	}
}
